import React from 'react'
import { Autocomplete, Box, Checkbox, Chip, TextField } from '@mui/material'
import { useDisabledInPreview } from '../../../dashboard/panelPreviewContext'
import { BILLING_CODE_LABELS, BillingCode, SELECTABLE_BILLING_CODES } from './billingCodes'

const optionLabel = (code: BillingCode) => `${code} — ${BILLING_CODE_LABELS[code]}`

// Unlike `BillingCodes.tsx`, this drops the one-mdd/adjustment-disorder-code-at-a-time rule.
const BillingCodesSelect = ({
  label = 'Billing codes',
  value,
  onChange,
  required,
  error,
}: {
  label?: string
  value: BillingCode[]
  onChange: (value: BillingCode[]) => void
  required?: boolean
  error?: string
}) => {
  const disabled = useDisabledInPreview()
  // A deprecated code on an existing note is still shown, though it is no longer offered.
  const options = [...SELECTABLE_BILLING_CODES, ...value.filter((code) => !SELECTABLE_BILLING_CODES.includes(code))]

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
          {value.map((code) => (
            <Chip
              key={code}
              size="small"
              label={optionLabel(code)}
              disabled={disabled}
              onDelete={() => onChange(value.filter((selected) => selected !== code))}
              sx={{ height: 'auto', maxWidth: '100%', py: 0.25, '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          ))}
        </Box>
      )}
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        disabled={disabled}
        options={options}
        value={value}
        onChange={(_event, next) => onChange(next)}
        getOptionLabel={optionLabel}
        ListboxProps={{ style: { maxHeight: 280 } }}
        renderOption={(props, code, { selected }) => (
          <li {...props}>
            <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
            {optionLabel(code)}
          </li>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={Boolean(error)}
            helperText={error ?? 'Search by code or name'}
          />
        )}
      />
    </Box>
  )
}

export default BillingCodesSelect
